// Pure proxy-commitment surfaces (COM-007–COM-009, plan §17.13).
//
// The consequence is confirmed before the mutation is sent, and provenance is one
// canonical rendering shared by cards, details, tooltips and the future print view.

import { Button, Dialog, Select, StatusMessage } from "@herefor/design-system";
import { PROXY_COMMITMENT_MESSAGES as COPY } from "@herefor/product-language";
import { useMemo, useState } from "react";

import { ClaimOutcome } from "../claim";
import type {
  ProxyAgreementSource,
  ProxyCommitmentState,
  ProxyRecordChoice,
} from "./useProxyCommitment";

export interface ProxyCandidate {
  readonly personId: string;
  readonly name: string;
}

export interface ProxyCommitmentProps {
  /** Server-decided capability. False means no affordance at all (AUT-007). */
  readonly canRecord: boolean;
  /** Server-derived coverage state; only controls whether a place is offered. */
  readonly stillNeedsSomeone: boolean;
  readonly candidates: readonly ProxyCandidate[];
  readonly actionsDisabled?: boolean;
  readonly state: ProxyCommitmentState;
  readonly onRecord: (choice: ProxyRecordChoice) => void;
}

const SOURCES = Object.keys(COPY.sources) as readonly ProxyAgreementSource[];

export function ProxyCommitmentForm({
  canRecord,
  stillNeedsSomeone,
  candidates,
  actionsDisabled = false,
  state,
  onRecord,
}: ProxyCommitmentProps) {
  const [open, setOpen] = useState(false);
  const [personId, setPersonId] = useState(candidates[0]?.personId ?? "");
  const [source, setSource] = useState<ProxyAgreementSource>("whatsapp");

  const person = useMemo(
    () => candidates.find((candidate) => candidate.personId === personId),
    [candidates, personId],
  );

  if (!canRecord) return null;
  if (!stillNeedsSomeone && state.status === "idle") return null;

  return (
    <section className="proxy-commitment">
      {state.status === "accepted" ? (
        <StatusMessage tone="success">
          {COPY.success(
            candidates.find((candidate) => candidate.personId === state.responsiblePersonId)
              ?.name ?? "",
          )}
        </StatusMessage>
      ) : null}
      {state.status === "refused" ? (
        <ClaimOutcome state={{ status: "refused", rejection: state.rejection }} />
      ) : null}

      {stillNeedsSomeone && candidates.length === 0 ? (
        <StatusMessage>{COPY.noEligiblePeople}</StatusMessage>
      ) : stillNeedsSomeone && state.status !== "accepted" ? (
        <Button
          variant="secondary"
          disabled={actionsDisabled || state.status === "submitting"}
          onClick={() => {
            setOpen(true);
          }}
        >
          {COPY.action}
        </Button>
      ) : null}

      <Dialog
        open={open}
        title={COPY.title}
        onClose={() => {
          setOpen(false);
        }}
        footer={
          <>
            <Button
              variant="primary"
              disabled={actionsDisabled || person === undefined || state.status === "submitting"}
              onClick={() => {
                if (person === undefined) return;
                setOpen(false);
                onRecord({ responsiblePersonId: person.personId, agreementSource: source });
              }}
            >
              {state.status === "submitting" ? COPY.working : COPY.confirm}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
              }}
            >
              {COPY.keepChecking}
            </Button>
          </>
        }
      >
        <p>{COPY.introduction}</p>
        <Select
          label={COPY.personLabel}
          value={personId}
          required
          options={candidates.map((candidate) => ({
            value: candidate.personId,
            label: candidate.name,
          }))}
          onChange={(event) => {
            setPersonId(event.target.value);
          }}
        />
        <Select
          label={COPY.sourceLabel}
          value={source}
          required
          options={SOURCES.map((value) => ({ value, label: COPY.sources[value] }))}
          onChange={(event) => {
            setSource(event.target.value as ProxyAgreementSource);
          }}
        />
        {person === undefined ? null : <p>{COPY.consequence(person.name, COPY.sources[source])}</p>}
      </Dialog>
    </section>
  );
}

export interface ProxyProvenanceProps {
  readonly helperName: string;
  readonly recorderName: string;
  /** New records use the typed codes; legacy synthetic fixtures remain renderable. */
  readonly agreementSource: string;
}

/** Canonical visible provenance. The title supplies the week-view tooltip seam. */
export function ProxyProvenance({
  helperName,
  recorderName,
  agreementSource,
}: ProxyProvenanceProps) {
  const text = COPY.provenance(helperName, recorderName, agreementSource);
  return (
    <span className="proxy-commitment__provenance" title={text}>
      {text}
    </span>
  );
}
